import { LineChartAnomalyValue } from '@modules/charts/types';
import { Dayjs } from 'dayjs';
import {
  CronInput,
  EqlField,
  IntermediateCsatFuncValue,
  IntermediateDsatFuncValue,
  IntermediateMeanFuncValue,
  IntermediateNpsFuncValue,
  TimeUnit,
} from 'generated/graphql';
import { ReactNode } from 'react';
import { FieldNames, SentimentType } from 'v2/core/entities/fields';
import { Scope } from 'v2/core/entities/scope';

export enum Category {
  Improvement = 'Improvement',
  Help = 'Help',
  Complaint = 'Complaint',
  Praise = 'Praise',
  Uncategorized = 'Uncategorized',
}

export interface Option {
  label: string;
  value: string;
}

export interface AggregateWindowOptions {
  date: Dayjs;
  aggregatePeriod?: TimeUnit;
}

export interface FieldNameOption {
  label: string;
  group: string;
  value: string;
  fieldName: FieldNames;
  qbField?: string;
  type: 'string' | 'date' | 'array' | 'number' | 'boolean';
  values?: { label: string; value: string | number | boolean }[];
}

export enum ArtefactType {
  Search = 'Search',
  Quantify = 'Quantify',
  Compare = 'Compare',
  Anomaly = 'Anomaly',
  Dashboard = 'Dashboard',
  Collection = 'Collection',
  Feed = 'Feed',
}

export interface QueryStringNonMetadataOptionFromFilter {
  fieldName: string;
  fieldDisplayName: string;
  values: string[];
  metadataKey?: undefined;
  operator?: string;
  eqlField?: EqlField;
}

export interface QueryStringMetadataOptionFromFilter {
  fieldName: string;
  fieldDisplayName: string;
  values: string[];
  metadataKey: string;
  operator: string;
  eqlField?: EqlField;
}

export type QueryStringOptionFromFilter =
  | QueryStringNonMetadataOptionFromFilter
  | QueryStringMetadataOptionFromFilter
  | undefined;

export type ZustandStoreWithDispatch<State, Action> = State & {
  dispatch: (action: Action) => Action;
};

export interface UnknownKeys {
  [key: string]: unknown;
}

export type SortBy = {
  field: string;
  isAsc: boolean;
};

export type SaveArtefactFunction = (inputs: {
  name: string;
  scope: Scope;
  shouldShowAsFeed?: boolean;
}) => Promise<
  | {
      artefactLink: string;
      artefactRoute: string;
    }
  | {
      artefactLink: null;
      artefactRoute: null;
    }
>;

export interface ReportDataPoint extends UnknownKeys {
  id: string;
  label: string;
  value: number;
  timestamp: number | undefined;
  isInvisible: boolean;
  isUpdateInProgress: boolean;
  entitySentiment: SentimentType | undefined;
  anomalyValue: LineChartAnomalyValue | undefined;
  isAnomalous: boolean;
  isRelevant: boolean;
  category: Category | undefined;
  nps?: Omit<IntermediateNpsFuncValue, '__typename'>;
  csat?: Omit<IntermediateCsatFuncValue, '__typename'>;
  mean?: Omit<IntermediateMeanFuncValue, '__typename'>;
  dsat?: Omit<IntermediateDsatFuncValue, '__typename'>;
  segmentDataPoints?: ReportDataPoint[];
  variants?: Record<
    string,
    {
      id: string;
      label: string;
      value: number;
      anomalyValue?: LineChartAnomalyValue;
      nps?: Omit<IntermediateNpsFuncValue, '__typename'>;
      csat?: Omit<IntermediateCsatFuncValue, '__typename'>;
      mean?: Omit<IntermediateMeanFuncValue, '__typename'>;
      dsat?: Omit<IntermediateDsatFuncValue, '__typename'>;
    }
  >;
}

export type GetChartLabelOptions = ({
  id,
  size,
}: {
  id: string;
  size?: number;
  hideSecondaryIcon?: boolean;
  showIndentForEmptySecondaryIcon?: boolean;
  shouldHideTooltip?: boolean;
}) => {
  startIcon: JSX.Element | null;
  endIcon: JSX.Element | null;
  label: string | null;
  isDisabled: boolean;
  messageNode: JSX.Element | null;
  labelTooltipTitle: ReactNode | null;
} | null;

export type CSSUnit = '%' | 'px' | 'em' | 'vh' | 'vw' | 'rem' | 'vmin' | 'vmax';

export enum Direction {
  Left,
  Top,
  Right,
  Bottom,
}

export type Cron = Pick<CronInput, 'realtime' | 'valueV2'>;
